/**
 * Centralized API URL resolution with protocol-aware logic
 * 
 * Ensures that all API calls use HTTPS when the page is loaded over HTTPS,
 * preventing mixed content errors.
 */

/**
 * Get the API URL with protocol matching the current page
 * 
 * This function ensures that:
 * - If the page is loaded over HTTPS, API calls also use HTTPS
 * - If the page is loaded over HTTP (localhost), API calls use HTTP
 * - Server-side rendering uses the environment variable as-is
 * 
 * @returns The API URL with the correct protocol
 */
export function getApiUrl(): string {
  let envApiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  
  // If we're in the browser, match the protocol to the current page
  if (typeof window !== 'undefined') {
    // Normalize the URL to ensure it has a protocol
    if (!envApiUrl.startsWith('http://') && !envApiUrl.startsWith('https://')) {
      // No protocol specified, add one based on current page
      envApiUrl = `${window.location.protocol}//${envApiUrl}`
    }
    
    // Force HTTPS if the page is loaded over HTTPS and URL starts with http://
    if (window.location.protocol === 'https:' && envApiUrl.startsWith('http://')) {
      return envApiUrl.replace(/^http:/, 'https:')
    }
    
    return envApiUrl
  }
  
  // Server-side: ensure protocol is present
  if (!envApiUrl.startsWith('http://') && !envApiUrl.startsWith('https://')) {
    // Default to https for production, http for localhost
    const protocol = envApiUrl.includes('localhost') ? 'http' : 'https'
    return `${protocol}://${envApiUrl}`
  }
  
  return envApiUrl
}

/**
 * Get the base path for API routes
 */
export function getBasePath(): string {
  return process.env.NEXT_PUBLIC_BASE_PATH || ''
}

/**
 * Construct a full API URL with protocol awareness
 * 
 * @param endpoint - The API endpoint (e.g., '/api/integrations')
 * @returns Full URL with correct protocol
 */
export function buildApiUrl(endpoint: string): string {
  const apiUrl = getApiUrl()
  const basePath = getBasePath()
  return `${apiUrl}${basePath}${endpoint}`
}
